import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectIntacctId } from "../../../UserAppSettings/selectors/userAppSettingsSelectors";
import { Connections, useHotglue } from "@hotglue/widget";
import { useAuth0 } from "@auth0/auth0-react";
import {
  CheckCircle,
  TrendingUp,
  Shield,
  Clock,
  FileText,
  Zap,
  ArrowRight,
} from "lucide-react";

const DataConnector: React.FC = () => {
  const intacctId = useSelector(selectIntacctId);
  const { setMetadata } = useHotglue();
  const { user } = useAuth0();

  useEffect(() => {
    if (intacctId && user) {
      setMetadata({
        application: "Hub Plus",
        ssoId: user.sub,
      });
    }
  }, [intacctId, user, setMetadata]);

  if (!intacctId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading connection information...</p>
        </div>
      </div>
    );
  }

  const benefits = [
    {
      icon: Clock,
      title: "Save Hours Every Month",
      description:
        "No more downloading, formatting, and emailing reports. Your financial data flows automatically to your coach.",
      color: "text-blue-600",
    },
    {
      icon: Shield,
      title: "Bank-Level Security",
      description:
        "Your connection is encrypted and secure. We never store your QuickBooks credentials.",
      color: "text-green-600",
    },
    {
      icon: FileText,
      title: "Automatic Report Generation",
      description:
        "Monthly Balance Sheets and P&L statements are generated and ready for review automatically.",
      color: "text-purple-600",
    },
    {
      icon: TrendingUp,
      title: "Real-Time Insights",
      description:
        "Your coach sees up-to-date financials, enabling better, more timely business guidance.",
      color: "text-orange-600",
    },
  ];

  const futureFeatures = [
    "Advanced Financial Dashboards",
    "Cash Flow Forecasting",
    "Automated Trend Analysis",
    "Custom KPI Tracking",
    "Comparative Industry Benchmarks",
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">QB</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              QuickBooks Data Connector
            </h1>
          </div>
          <p className="text-gray-600">
            Automatically sync your financial data from QuickBooks to
            CertainPath Hub+
          </p>
        </div>

        {/* Data Flow */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            The Data Connector securely links your QuickBooks Online account to
            CertainPath Hub+, automatically syncing your financial reports each
            month.
          </p>
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col items-center flex-1">
              <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center shadow-sm mb-2">
                <span className="text-white font-bold text-lg">QB</span>
              </div>
              <span className="text-sm font-semibold text-gray-900 mb-1">
                QuickBooks Online
              </span>
              <p className="text-xs text-gray-600 text-center">
                Your financial data source
              </p>
            </div>

            <div className="flex flex-col items-center justify-start pt-6">
              <ArrowRight className="w-6 h-6 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-500 mt-1">Secure OAuth</span>
            </div>

            <div className="flex flex-col items-center flex-1">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center shadow-sm mb-2 relative">
                <Zap className="w-8 h-8 text-white" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
              </div>
              <span className="text-sm font-semibold text-gray-900 mb-1">
                Data Connector
              </span>
              <p className="text-xs text-gray-600 text-center">
                Automated monthly sync
              </p>
            </div>

            <div className="flex flex-col items-center justify-start pt-6">
              <ArrowRight className="w-6 h-6 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-500 mt-1">Real-time</span>
            </div>

            <div className="flex flex-col items-center flex-1">
              <div className="w-16 h-16 bg-purple-600 rounded-lg flex items-center justify-center shadow-sm mb-2">
                <span className="text-white font-bold text-sm">Hub+</span>
              </div>
              <span className="text-sm font-semibold text-gray-900 mb-1">
                CertainPath Hub+
              </span>
              <p className="text-xs text-gray-600 text-center">
                Reports available to you & your coach
              </p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  What Gets Synced
                </h4>
                <p className="text-xs text-gray-600">
                  Balance Sheets and Profit & Loss statements are automatically
                  generated and synced monthly. Your coach receives instant
                  access to review your financials without you having to
                  manually download, format, or email anything.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Manage Connections
          </h2>

          <div className="mb-4">
            <Connections tenant={intacctId} />
          </div>

          <div className="flex items-start gap-2 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span>
              Secure OAuth connection with read-only access. We never store your
              QuickBooks credentials.
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Benefits</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            {benefits.map((benefit, index) => (
              <div
                className="bg-gray-50 rounded-lg p-4 flex-1 min-w-[200px] max-w-[280px]"
                key={index}
              >
                <div
                  className={`w-10 h-10 rounded-lg bg-gradient-to-br ${
                    index === 0
                      ? "from-blue-100 to-blue-200"
                      : index === 1
                        ? "from-green-100 to-green-200"
                        : index === 2
                          ? "from-purple-100 to-purple-200"
                          : "from-orange-100 to-orange-200"
                  } flex items-center justify-center mb-3`}
                >
                  <benefit.icon className={`w-5 h-5 ${benefit.color}`} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  {benefit.title}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Available Reports
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-sm mb-1">
                  Monthly Balance Sheet
                </h4>
                <p className="text-xs text-gray-600">
                  Complete overview of assets, liabilities, and equity -
                  automatically generated each month.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-sm mb-1">
                  Profit & Loss Statement
                </h4>
                <p className="text-xs text-gray-600">
                  Detailed income and expense tracking with automatic monthly
                  updates.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Coming in 2026
            </h2>
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
              ROADMAP
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            We&apos;re building powerful financial dashboards that will give you
            unprecedented insights into your business.
          </p>
          <div className="grid md:grid-cols-2 gap-2">
            {futureFeatures.map((feature, index) => (
              <div
                className="flex items-center gap-2 text-sm text-gray-700"
                key={index}
              >
                <CheckCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataConnector;
